import clsx from "clsx";
import { format, isSameDay } from "date-fns";
import { de } from "date-fns/locale";
import { useEffect, useRef } from "react";

import type { CalendarDay } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { HolidayAndAbsenceBars } from "~/modules/dashboard/components/TimeTrackingCalendar/HolidayAndAbsenceBars.tsx";
import { TrackingRow } from "~/modules/dashboard/components/TimeTrackingCalendar/TrackingRow.tsx";

type Props = {
	days: CalendarDay[];
	selectedDate: Date;
	selectDate: (date: Date) => void;
};

/**
 * The left column of the day view: every day of the month in a list that scrolls on its own. The rows inside
 * are plain, because a button holds them and a button cannot hold a popover or another button.
 * A weekend is dimmed, and its holiday name is one step darker so that it stays readable on the dimmed background.
 */
export function DayColumn({ days, selectedDate, selectDate }: Props) {
	const selectedRef = useRef<HTMLButtonElement>(null);
	// The key is a string, because `selectedDate` can be a new `Date` of the same day on each render.
	const selectedKey = format(selectedDate, "yyyy-MM-dd");
	// "nearest" leaves an entry that is in view where it is, so a click does not move the list under the pointer.
	// A step with the header chevrons can select an entry out of view.
	useEffect(() => {
		selectedRef.current?.scrollIntoView({ block: "nearest" });
	}, [selectedKey]);

	return (
		<div className="flex h-[32rem] w-64 shrink-0 flex-col gap-2 overflow-y-auto p-1">
			{days.filter((day) => day.isInMonth).map((day) => {
				const isSelected = isSameDay(day.date, selectedDate);
				return (
					<button key={day.date.toISOString()}
							ref={isSelected ? selectedRef : undefined}
							type="button"
							aria-pressed={isSelected}
							onClick={() => selectDate(day.date)}
							className={clsx("flex min-w-0 flex-col gap-1 rounded-md border p-2 text-left text-xs",
								isSelected ? "border-gray-300 bg-gray-200" : "border-gray-200 hover:border-gray-400 hover:bg-gray-100",
								!isSelected && day.isWeekend && "bg-gray-50")}>
						<span className={clsx("font-semibold", day.isToday ? "text-secondary-500" : "text-gray-700")}>
							{format(day.date, "EEE, d.", { locale: de })}
							{day.holiday && <span className={clsx("ml-1 font-normal", day.isWeekend ? "text-gray-500" : "text-gray-400")}>{day.holiday.displayName}</span>}
						</span>
						<HolidayAndAbsenceBars day={day}
											   size="sm"
											   hideHoliday />
						{day.timeTrackings.map((timeTracking) => (
							<TrackingRow key={timeTracking.id}
										 timeTracking={timeTracking}
										 size="sm" />
						))}
					</button>
				);
			})}
		</div>
	);
}
